import { FC, memo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid, Typography } from '@mui/material';
import { isEqual } from 'lodash';

import { AutocompleteControl } from 'components/_basic/Form/Autocomplete';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { ISectionRowProps as IProps } from 'components/BusinessToolForm';

import { concatWithDot } from 'helpers/string';

import {
  ILevel2Department,
  LEVEL2_DEPARTMENT_NAMES,
  LEVEL3_DEPARTMENT_NAMES,
  prefix,
  STAFF_ORGANIZATION_NAMES,
} from '../utils/form';

export const DepartmentPosition: FC<IProps> = memo(({ name, disabled = false }) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();
  const selectedDepartmentIndex = useRef(-1);
  const previousDepartments = useRef<null | string[]>(null);
  const hasDepartmentValueDeleted = useRef(false);

  const departmentOptions = getValues(concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL2_DEPARTMENT)).map(
    (item: ILevel2Department) => item[LEVEL2_DEPARTMENT_NAMES.DEPARTMENT],
  );
  const belowDepartmentsValue = getValues(concatWithDot(name, LEVEL3_DEPARTMENT_NAMES.BELOW_DEPARTMENT));

  const isCurrentDepartmentsChanged =
    previousDepartments.current &&
    departmentOptions.length === previousDepartments.current.length &&
    !isEqual(departmentOptions, previousDepartments.current) &&
    departmentOptions.current !== -1;

  const isDepartmentsCountDecreased =
    previousDepartments.current &&
    departmentOptions.length < previousDepartments.current.length &&
    selectedDepartmentIndex.current !== -1;

  if (isCurrentDepartmentsChanged) {
    if (typeof departmentOptions[selectedDepartmentIndex.current] === 'string') {
      setValue(
        concatWithDot(name, LEVEL3_DEPARTMENT_NAMES.BELOW_DEPARTMENT),
        departmentOptions[selectedDepartmentIndex.current],
      );
    }
  }
  if (isDepartmentsCountDecreased && previousDepartments.current) {
    previousDepartments.current.forEach((feature, index) => {
      if (!departmentOptions.includes(feature)) {
        if (index === selectedDepartmentIndex.current)
          setValue(concatWithDot(name, LEVEL3_DEPARTMENT_NAMES.BELOW_DEPARTMENT), '');
        selectedDepartmentIndex.current === -1;
        hasDepartmentValueDeleted.current = true;
      }
    });
  }

  if (!belowDepartmentsValue) {
    selectedDepartmentIndex.current = -1;
  }
  if (!hasDepartmentValueDeleted.current && belowDepartmentsValue) {
    selectedDepartmentIndex.current = departmentOptions.indexOf(belowDepartmentsValue);
  }
  hasDepartmentValueDeleted.current = false;
  previousDepartments.current = departmentOptions;

  return (
    <Grid container spacing={2}>
      <Grid item xs>
        <TextInputControl
          control={control}
          id={concatWithDot(name, LEVEL3_DEPARTMENT_NAMES.DEPARTMENT)}
          name={concatWithDot(name, LEVEL3_DEPARTMENT_NAMES.DEPARTMENT)}
          label={t('management.staff.departmentPosition')}
          disabled={disabled}
          maxLength={50}
        />
      </Grid>
      <Grid item sx={{ textAlign: 'center', marginTop: '28px' }}>
        <Typography variant="body2">{t('management.staff.below')}</Typography>
      </Grid>
      <Grid item xs>
        <AutocompleteControl
          id={concatWithDot(name, LEVEL3_DEPARTMENT_NAMES.BELOW_DEPARTMENT)}
          name={concatWithDot(name, LEVEL3_DEPARTMENT_NAMES.BELOW_DEPARTMENT)}
          options={departmentOptions}
          label={t('management.staff.departmentPosition')}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
});
